import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { setProjectIdentity } from '@store/actionSlices/projectIdentity'
import { RootStateTypeExtra } from '@store/types'

import { useLazyGetMasterKeyQuery } from '@api/master-key'

const useResolveMasterKeyRoom = () => {
  const dispatch = useDispatch()
  const currentMasterKey = useSelector(
    (state: RootStateTypeExtra) => state.projectIdentity.masterKey
  )
  const { _id: userId } = useSelector(
    (state: RootStateTypeExtra) => state.token
  )
  const isShowcaseMultiscreen = useSelector(
    (state: RootStateTypeExtra) => state.projectIdentity.isShowcaseMultiscreen
  )
  const [getMasterKey] = useLazyGetMasterKeyQuery()

  const resolveMasterKeyRoom = useCallback(
    async (
      projectName: string,
      options?: {
        masterKey?: string
        userId?: string
        accessToken?: string
      }
    ): Promise<{
      room: string | undefined
      isMasterKeyMultiscreen: boolean
    }> => {
      const activeMasterKey = options?.masterKey || currentMasterKey
      const activeUserId = options?.userId || userId
      const activeAccessToken = options?.accessToken

      const shouldCheckMultiscreen = !options?.masterKey

      if (
        activeMasterKey &&
        activeUserId &&
        (!shouldCheckMultiscreen || isShowcaseMultiscreen)
      ) {
        try {
          const masterKeyResponse = await getMasterKey({
            masterKey: activeMasterKey,
            userId: activeUserId,
            ...(activeAccessToken && { accessToken: activeAccessToken }),
          }).unwrap()
          const masterKeyData = masterKeyResponse.data

          if (masterKeyData && masterKeyData.isChild) {
            const masterKeyRoom =
              masterKeyData.sessionKeys?.find(
                (sk) => sk.projectId === projectName
              )?.room || ''

            dispatch(
              setProjectIdentity({
                isShowcaseMultiscreen: masterKeyData.isChild,
              })
            )

            return {
              room: masterKeyRoom,
              isMasterKeyMultiscreen: masterKeyData.isChild,
            }
          }
        } catch (e) {
          console.error('Error fetching master key room:', e)
        }
      }

      return {
        room: undefined,
        isMasterKeyMultiscreen: false,
      }
    },
    [currentMasterKey, userId, isShowcaseMultiscreen, getMasterKey, dispatch]
  )

  return resolveMasterKeyRoom
}

export default useResolveMasterKeyRoom
